import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Kbd } from '@etnas/design-system';
import { useAuth } from '../auth';
import { workspaceLanding } from '../workspaces';
import { usePatientSlots } from '../patientSlots';
import { useTheme, type ThemePref } from '../theme';
import { useToast } from '@/components/Toaster';
import { Popover } from './Popover';
import { API_MODE } from '../config';
import { useConnectivity } from './connectivity';

/**
 * Top bar (zone 3) built on the DS `ex-top` markup with working controls:
 * working-context switcher (explicit role + location switch), command search (⌘K), status, account menu.
 */
export function AppTopBar({ onSearch }: { onSearch?: () => void }) {
  const { me, switchContext, logout, lockNow } = useAuth();
  const { slots, confirmLeave, dirtyPatients } = usePatientSlots();
  const navigate = useNavigate();
  const toast = useToast();
  const [theme, setTheme] = useTheme();
  const online = useConnectivity();
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const [pendingLoc, setPendingLoc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  const ctx = me.context;

  const apply = async (close: () => void) => {
    const roleId = pendingRole ?? ctx.roleId;
    const locationId = pendingLoc ?? ctx.locationId;
    if (roleId === ctx.roleId && locationId === ctx.locationId) return close();
    for (const id of dirtyPatients()) if (!(await confirmLeave(id))) return;
    setBusy(true);
    try {
      const m = await switchContext(roleId, locationId);
      close();
      toast.info(`Now working as ${m.context.role.name}`, `${m.context.location?.name ?? 'No location'} · lists reloaded for this role`);
      navigate(workspaceLanding(m.context.roleId, m.context.role.key));
    } catch (e) {
      toast.error(e, 'Could not change working context');
    } finally {
      setBusy(false);
    }
  };

  return (
    <header className="ex-top">
      <Popover
        label="Working context"
        trigger={({ toggle, props }) => (
          <button type="button" className="ex-top-context" onClick={toggle} {...props} aria-label={`Working context: ${ctx.location?.facility ?? me.tenant?.name ?? 'Platform'}, ${ctx.role.name}. Change`}>
            <Icon name="building" size={16} />
            <span>
              <b>{ctx.location?.facility ?? me.tenant?.name ?? 'ETNAS platform'}</b>
              <span className="ex-fg-muted"> · {ctx.location?.department ?? (me.tenant ? me.tenant.name : 'Operations')}</span>
            </span>
            <span className="ex-top-role">{ctx.role.name}</span>
            <Icon name="chevron-down" size={14} />
          </button>
        )}
      >
        {(close) => (
          <div className="ex-stack is-tight" style={{ minWidth: 300 }}>
            <div className="ex-menu-head">
              <div className="ex-lbl">Organization</div>
              <div className="ex-strong">{me.tenant?.name ?? 'Platform (no tenant)'}</div>
            </div>
            {me.roles.length > 1 ? (
              <div role="radiogroup" aria-label="Role">
                <div className="ex-lbl ex-menu-head">Role — switch explicitly; permissions never merge</div>
                {me.roles.map((r) => (
                  <button key={r.id} type="button" role="radio" aria-checked={(pendingRole ?? ctx.roleId) === r.id} className="ex-menu-item" onClick={() => setPendingRole(r.id)}>
                    <Icon name={(pendingRole ?? ctx.roleId) === r.id ? 'check' : 'user'} size={14} />
                    {r.name}
                  </button>
                ))}
              </div>
            ) : (
              <div className="ex-menu-head">
                <div className="ex-lbl">Role</div>
                <div>{ctx.role.name}</div>
              </div>
            )}
            {me.locations.length ? (
              <div role="radiogroup" aria-label="Location">
                <div className="ex-lbl ex-menu-head">Location</div>
                {me.locations.map((l) => (
                  <button key={l.id} type="button" role="radio" aria-checked={(pendingLoc ?? ctx.locationId) === l.id} className="ex-menu-item" onClick={() => setPendingLoc(l.id)}>
                    <Icon name={(pendingLoc ?? ctx.locationId) === l.id ? 'check' : 'building'} size={14} />
                    <span className="ex-col">
                      <span>{l.name}</span>
                      <span className="ex-caption ex-fg-muted">
                        {l.facility} · {l.department}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ex-ctxnote">Changing role or location reloads what you may see. Unsaved work asks first.</div>
            <div className="ex-cluster is-end" style={{ padding: '0 4px 4px' }}>
              <Button size="sm" variant="quiet" onClick={() => { setPendingRole(null); setPendingLoc(null); close(); }}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" loading={busy} onClick={() => apply(close)}>
                Switch context
              </Button>
            </div>
          </div>
        )}
      </Popover>

      {onSearch ? (
        <button type="button" className="ex-top-search" onClick={onSearch} aria-keyshortcuts="Meta+K Control+K">
          <Icon name="search" size={16} />
          <span>Search patients and actions</span>
          <span className="ex-top-kbd">
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>
      ) : (
        <span className="ex-spacer" />
      )}

      <div className="ex-top-right">
        {API_MODE === 'mock' ? <span className="ex-envtag">Synthetic</span> : null}
        <span className={'ex-top-status ' + (online ? 'is-ok' : 'is-degraded')} role="status">
          <span className="ex-statusdot" />
          {online ? 'All systems normal' : 'Offline — changes kept'}
        </span>
        <Popover
          label="Account"
          align="right"
          trigger={({ toggle, props }) => (
            <button type="button" className="ex-top-user" onClick={toggle} {...props} aria-label={`Account: ${me.user.displayName}`}>
              <span className="ex-top-avatar">{me.user.initials}</span>
              <span className="ex-top-uname">{me.user.displayName}</span>
            </button>
          )}
        >
          {(close) => (
            <div className="ex-stack is-tight">
              <div className="ex-menu-head">
                <div className="ex-strong">{me.user.displayName}</div>
                <div className="ex-caption ex-fg-muted ex-mono">{me.user.username}</div>
              </div>
              <div role="radiogroup" aria-label="Theme">
                <div className="ex-lbl ex-menu-head">Theme</div>
                {(['system', 'light', 'dark'] as ThemePref[]).map((t) => (
                  <button key={t} type="button" role="radio" aria-checked={theme === t} className="ex-menu-item" onClick={() => setTheme(t)}>
                    <Icon name={theme === t ? 'check' : 'eye'} size={14} />
                    {t === 'system' ? 'Follow system' : t === 'light' ? 'Light' : 'Dark'}
                  </button>
                ))}
              </div>
              <hr className="ex-divider" />
              {me.tenant && (['provider', 'nurse', 'front_desk', 'tenant_admin'].includes(ctx.role.key) || me.permissions.includes('downtime.reconcile')) ? (
                <button type="button" className="ex-menu-item" onClick={() => { close(); navigate(ctx.role.key === 'tenant_admin' ? '/admin/downtime' : '/downtime'); }}>
                  <Icon name="offline" size={14} />
                  {me.permissions.includes('downtime.reconcile') ? 'Downtime reconciliation and packets' : 'Downtime packets on this device'}
                </button>
              ) : null}
              {me.permissions.includes('ai.governance.signoff') && ctx.role.key !== 'ai_governance' ? (
                <button type="button" className="ex-menu-item" onClick={() => { close(); navigate('/ai-signoff'); }}>
                  <Icon name="spark" size={14} />
                  AI governance sign-off
                </button>
              ) : null}
              <button type="button" className="ex-menu-item" onClick={() => { close(); lockNow('idle'); }}>
                <Icon name="lock" size={14} />
                Lock screen
              </button>
              <button type="button" className="ex-menu-item" onClick={async () => { close(); for (const s of slots) if (!(await confirmLeave(s.patientId))) return; await logout(); navigate('/login'); }}>
                <Icon name="external" size={14} />
                Sign out
              </button>
            </div>
          )}
        </Popover>
      </div>
    </header>
  );
}
